import React from 'react';
import { Bar } from 'react-chartjs-2';
import { MemberActivityData } from '../types';
import styles from '../../../../../../styles/OverviewTab.module.css';

interface TopPerformersChartProps {
  data: MemberActivityData[];
}

const TopPerformersChart: React.FC<TopPerformersChartProps> = ({ data }) => {
  const topPerformers = data.slice(0, 8); // Top 8 members for better visibility
  
  // Create colors matching the dashboard style
  const getBarColor = (index: number, total: number) => {
    // Use blue shades similar to the dashboard
    const colors = [
      '#3b82f6', // Primary blue
      '#60a5fa', // Light blue
      '#93c5fd', // Lighter blue
      '#2563eb', // Dark blue
      '#1d4ed8', // Darker blue
      '#1e40af', // Very dark blue
      '#1e3a8a', // Deep blue
      '#172554', // Deepest blue
    ];
    return colors[index % colors.length];
  };

  const getBorderColor = (index: number) => {
    return '#ffffff';
  };

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold' as const,
          family: 'var(--font-main)',
        },
        bodyFont: {
          size: 12,
          weight: 'normal' as const,
          family: 'var(--font-main)',
        },
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context: any) => {
            return context[0].label;
          },
          label: (context: any) => {
            const index = context.dataIndex;
            const member = topPerformers[index];
            const labels: string[] = [`Tasks Completed: ${context.parsed.x}`];
            if (member && member.points_earned > 0) {
              labels.push(`Points Earned: ${member.points_earned}`);
            }
            return labels;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
          drawBorder: false,
          lineWidth: 1,
        },
        ticks: {
          font: {
            size: 11,
            family: 'var(--font-main)',
          },
          color: '#6b7280',
          stepSize: 1,
          precision: 0,
        },
        border: {
          display: true,
          color: '#e5e7eb',
        },
      },
      y: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'var(--font-main)',
            weight: 'normal' as const,
          },
          color: '#374151',
          maxRotation: 0,
          padding: 8,
        },
        border: {
          display: true,
          color: '#e5e7eb',
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 8,
        borderSkipped: false,
      },
    },
  };

  const chartData = {
    labels: topPerformers.map((m) => {
      // Truncate long names
      return m.name.length > 20 ? m.name.substring(0, 20) + '...' : m.name;
    }),
    datasets: [
      {
        label: 'Completed Tasks',
        data: topPerformers.map(m => m.completed_tasks),
        backgroundColor: topPerformers.map((_, index) => getBarColor(index, topPerformers.length)),
        borderColor: topPerformers.map((_, index) => getBorderColor(index)),
        borderWidth: 2,
        hoverBackgroundColor: topPerformers.map((_, index) => {
          const baseColor = getBarColor(index, topPerformers.length);
          // Slightly lighter on hover
          return baseColor;
        }),
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3,
      },
    ],
  };

  return (
    <div className={styles.overviewCard}>
      <h3 className={styles.cardTitle}>
        <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        Top Performers
      </h3>
      <div className={styles.chartContainer}>
        {data && data.length > 0 ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <div className={styles.chartPlaceholder}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>No data available</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopPerformersChart;

